import React, { useState, useCallback, useMemo, useEffect } from 'react'
import { Box } from '@hub/box'
import { Stack } from '@hub/stack'
import { CoreContainer } from '@hub/core-container'
import { useToken } from '@hub/design-system-base'
import { Centre as CentreType } from '../../../../types/centre'
import {
  CentreSelector as SharedCentreSelector,
  getCentreDisplayTitle,
  Centre,
  MAX_SUGGESTIONS,
} from '../../../components/centre-selector'
import { useRecentCentres } from '../../../hooks/use-recent-centres'
import analyticsUtils from '../../../helpers/analytics-utils'
import trackEvent from '../../../helpers/analytics-utils/track-event'
import { VisitCentreTabPanel } from './visit-centre-tab-panel'
import navStyle from './navStyle.module.scss'

const centreSelectorConfig = {
  allCentresLabel: 'All centres',
  inputLabel: 'Find a centre',
  locationLabel: 'Or use your current location',
}

interface FindACentreNavPanelProps {
  centres: CentreType[]
  centre?: CentreType
}

export const FindACentreNavPanel: React.FC<FindACentreNavPanelProps> = ({
  centres,
  centre: currentCentre,
}) => {
  const menuZIndex = useToken('zIndices', 'menu')
  const [isOpenCentre, setIsOpenCentre] = useState<boolean>(false)
  const toggleOpenCentre = useCallback(() => {
    const navAction = isOpenCentre
      ? analyticsUtils.actions.UNIVERSAL_HEADER_NAV_CLOSE
      : analyticsUtils.actions.UNIVERSAL_HEADER_NAV_OPEN
    setIsOpenCentre(!isOpenCentre)
    trackEvent(
      analyticsUtils.categories.UNIVERSAL_HEADER,
      navAction,
      `Find a centre ${isOpenCentre ? 'close' : 'open'}`
    )
  }, [isOpenCentre])

  // Inject a `.displayTitle` field for better rendering
  const centresWithDisplayTitle: Centre[] = useMemo(
    () =>
      centres.map(centre => ({
        ...centre,
        displayTitle: getCentreDisplayTitle(centre),
      })),
    [centres]
  )

  const recentCentres: string[] = useRecentCentres()

  const [suggestedCentres, setSuggestedCentres] = useState<Centre[]>([])

  useEffect(() => {
    setSuggestedCentres(
      centresWithoutCurrent(
        currentCentre,
        recentCentres,
        centresWithDisplayTitle
      )
    )
  }, [currentCentre, recentCentres, centresWithDisplayTitle])

  return (
    <>
      <CoreContainer
        fadeOutOverflow
        as={props => (
          <Stack
            {...props}
            direction="row"
            align="center"
            shouldWrapChildren={false}
          />
        )}
        sx={{
          height: 'size-full',
          fontSize: 'font-sm',
          overflowX: 'scroll',
          position: 'relative',

          /* Hide scrollbar for Chrome, Safari and Opera */
          '&::-webkit-scrollbar': {
            display: 'none',
          },

          /* Hide scrollbar for IE, Edge and Firefox */
          msOverflowStyle: 'none' /* IE and Edge */,
          scrollbarWidth: 'none' /* Firefox */,
          zIndex: menuZIndex,
        }}
      >
        <VisitCentreTabPanel
          key="visit-centre"
          suggestedCentres={suggestedCentres}
          toggleOpenCentre={toggleOpenCentre}
          isOpenCentre={isOpenCentre}
        />
      </CoreContainer>
      {/* open this component for find a centre */}
      {isOpenCentre && (
        <Box
          sx={{
            bg: 'white',
            position: 'relative',
            zIndex: 'dropdown',
            boxShadow: '0px 16px 16px 0px rgba(0,0,0,0.15)',
          }}
        >
          <CoreContainer>
            <SharedCentreSelector
              callBackSetSuggestedCentres={setSuggestedCentres}
              centres={centres}
              navStyle={navStyle}
              config={centreSelectorConfig}
            />
          </CoreContainer>
        </Box>
      )}
    </>
  )
}

function centresWithoutCurrent(
  currentCentre: CentreType | undefined,
  recentCentres: string[],
  centresWithDisplayTitle: Centre[]
): Centre[] {
  // Filter out the current centre from suggestions
  const filteredSlugs = currentCentre
    ? recentCentres.filter(slug => slug !== currentCentre.slug)
    : recentCentres

  return (
    filteredSlugs
      // Convert it into a complete object
      .map(slug => centresWithDisplayTitle.find(centre => centre.slug === slug))
      // Clear out any bad data
      .filter(centre => Boolean(centre))
      // Limit the number of suggestions we return
      .slice(0, MAX_SUGGESTIONS) as Centre[]
  )
}
